import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Printer, ArrowLeft, XCircle, CheckCircle, Home, ShoppingBag, 
  Building, CreditCard, Calendar, Clock, Hash, FileText, Shield,
  Package, BadgeCheck, User
} from 'lucide-react';
import { useAktiviteTakip } from '../hooks/useAktiviteTakip';
import { getBankLogo } from '../constants/cardBankLogos';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OdemeSonuc = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { donusumAdimiKaydet, siparisKaydet, isAktif: isMusteriHook } = useAktiviteTakip();
  const odemeKaydedildiRef = useRef(false);
  const sepetTemizlendiRef = useRef(false);
  const siparisOlusturulduRef = useRef(false);
  
  // URL Parametreleri
  const status = searchParams.get('status');
  const orderNo = searchParams.get('order_no') || '-';
  const invoiceId = searchParams.get('invoice_id') || '-';
  const total = searchParams.get('total') || '0';
  const authCode = searchParams.get('auth_code') || '-';
  const errorMsg = searchParams.get('error') || '';
  const hataKategori = searchParams.get('hata_kategori') || '';
  const hataKategoriEtiket = decodeURIComponent(searchParams.get('hata_kategori_etiket') || '');
  const hataOneri = decodeURIComponent(searchParams.get('hata_oneri') || '');
  const qnbKod = searchParams.get('qnb_kod') || searchParams.get('status_code') || '';
  const musteriAdi = searchParams.get('musteri') || '';
  const kartSahibi = searchParams.get('kart_sahibi') || '';
  const cariKodu = searchParams.get('cari') || searchParams.get('cari_kodu') || '';
  const cariUnvani = searchParams.get('cari_unvani') || '';
  const taksitSayisi = parseInt(searchParams.get('taksit') || '1') || 1;
  const islemYapan = searchParams.get('islem_yapan') || '';
  const siparisTipi = searchParams.get('siparis_tipi') || searchParams.get('odeme_tipi') || '';
  const panelKodu = searchParams.get('panel_kodu') || '';
  
  // Banka / Kart bilgileri
  const banka = decodeURIComponent(searchParams.get('banka') || '');
  const kartTipi = decodeURIComponent(searchParams.get('kart_tipi') || '');
  
  // Firma Bilgileri (URL'den; eksik ise API'den auto-fetch)
  const firma = (searchParams.get('firma') || 'OTOMOTIV').toUpperCase();
  const urlFirmaUnvan = searchParams.get('firma_unvan');
  const urlFirmaAdres = searchParams.get('firma_adres');
  const urlFirmaVergiNo = searchParams.get('firma_vergi_no');
  const urlFirmaVergiDairesi = searchParams.get('firma_vergi_dairesi');

  // FALLBACK defaults (firma için en son çare)
  const FIRMA_FALLBACK = {
    OTOMOTIV: {
      unvan: 'YETSAN OTOMOTİV SAN. TİC. LTD. ŞTİ.',
      adres: '209.CAD NO:15 MACUNKÖY Yenimahalle Ankara',
      vergi_no: '9530015004',
      vergi_dairesi: 'OSTİM VERGİ DAİRESİ',
    },
    KONYA: {
      unvan: 'KONYA RADYATÖR OTO KAL.SAN.TİC.LTD.ŞTİ.',
      adres: 'HOROZLUHAN MAH.SELÇUKLU CAD.UZMAN SAN.SİT.NO:206 SELÇUKLU/KONYA',
      vergi_no: '5770821841',
      vergi_dairesi: 'SELÇUK VERGİ DAİRESİ',
    },
    KAYSERI: {
      unvan: 'KAYSERİ RADYATÖR OTO KALORİFER SAN.TİC.LTD.ŞTİ.',
      adres: 'ŞEKER MAH.YENİ SANAYİ SİTESİ 6120.SOK.No:4 KOCASİNAN / KAYSERİ',
      vergi_no: '5400601230',
      vergi_dairesi: 'MİMARSİNAN VERGİ DAİRESİ',
    },
    // Çorum firma bilgileri ayarlardan (qnbpay firma_bilgileri) girilir → API ile dolar.
    CORUM: {
      unvan: 'YETSAN ÇORUM',
      adres: '',
      vergi_no: '',
      vergi_dairesi: '',
    },
  };
  const fallback = FIRMA_FALLBACK[firma] || FIRMA_FALLBACK.OTOMOTIV;

  // State: önce URL'den, eksikse fallback. Eksikse API'den de çek (aşağıda).
  const [firmaBilgileri, setFirmaBilgileri] = useState({
    unvan: urlFirmaUnvan ? decodeURIComponent(urlFirmaUnvan) : fallback.unvan,
    adres: urlFirmaAdres ? decodeURIComponent(urlFirmaAdres) : fallback.adres,
    vergi_no: urlFirmaVergiNo || fallback.vergi_no,
    vergi_dairesi: urlFirmaVergiDairesi ? decodeURIComponent(urlFirmaVergiDairesi) : fallback.vergi_dairesi,
  });

  const firmaUnvan = firmaBilgileri.unvan;
  const firmaAdres = firmaBilgileri.adres;
  const firmaVergiNo = firmaBilgileri.vergi_no;
  const firmaVergiDairesi = firmaBilgileri.vergi_dairesi;

  // URL'de firma_unvan yoksa AMA firma kodu varsa, API'den canlı bilgileri çek (DB override + defaults)
  useEffect(() => {
    if (urlFirmaUnvan) return; // URL zaten doğru bilgi taşıyor
    if (!firma || !FIRMA_FALLBACK[firma]) return;
    let cancel = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/panel/qnbpay-firma-config/${firma}`);
        if (cancel) return;
        const fb = res.data?.firma_bilgileri;
        if (fb && (fb.unvan || fb.vergi_no)) {
          setFirmaBilgileri({
            unvan: fb.unvan || fallback.unvan,
            adres: fb.adres || fallback.adres,
            vergi_no: fb.vergi_no || fallback.vergi_no,
            vergi_dairesi: fb.vergi_dairesi || fallback.vergi_dairesi,
          });
        }
      } catch (e) {
        // sessizce fallback'te kal
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma, urlFirmaUnvan]);

  // Firma bazlı email & kart ekstre etiketi (KAYSERI/KONYA/OTOMOTIV)
  const FIRMA_META = {
    KONYA: { email: 'yetsankonya@yetsan.com.tr', ekstre: 'KONYA RADYATOR' },
    KAYSERI: { email: 'yetsankayseri@yetsan.com.tr', ekstre: 'KAYSERI RADYATOR' },
    CORUM: { email: 'corum@yetsan.com.tr', ekstre: 'YETSAN CORUM' },
    OTOMOTIV: { email: 'otomotiv@yetsan.com.tr', ekstre: 'YETSAN' },
  };
  const firmaMeta = FIRMA_META[firma] || FIRMA_META.OTOMOTIV;
  
  const isSuccess = status === 'success';
  const isMusteri = user?.role === 'musteri' || user?.cari_kodu || siparisTipi === 'sepet' || siparisTipi === 'siparis' || siparisTipi === 'siparis_odemesi';
  const isSiparisOdemesi = siparisTipi === 'sepet' || siparisTipi === 'siparis' || siparisTipi === 'siparis_odemesi';
  
  // Müşteri No
  const musteriNo = panelKodu || user?.panel_kodu || cariKodu || '';
  
  const now = new Date();
  const tarih = now.toLocaleDateString('tr-TR');
  const saat = now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const handlePrint = () => window.print();
  const formatTutar = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString('tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2});
  };

  // Ödeme başarılı olduğunda bekleyen siparişi oluştur
  useEffect(() => {
    const siparisOlustur = async () => {
      // SessionStorage'da bekleyen sipariş varsa veya siparisTipi uygunsa sipariş oluştur
      const bekleyenSiparisStr = sessionStorage.getItem('bekleyen_siparis');
      const shouldCreateOrder = isSuccess && (isSiparisOdemesi || bekleyenSiparisStr) && !siparisOlusturulduRef.current;
      
      console.log('[Sipariş Debug] isSuccess:', isSuccess, 'isSiparisOdemesi:', isSiparisOdemesi, 'siparisTipi:', siparisTipi, 'hasBekleyenSiparis:', !!bekleyenSiparisStr, 'shouldCreateOrder:', shouldCreateOrder);
      
      if (shouldCreateOrder) {
        siparisOlusturulduRef.current = true;
        
        try {
          if (!bekleyenSiparisStr) {
            console.log('[Sipariş] Bekleyen sipariş bulunamadı - sessionStorage boş');
            return;
          }
          
          const bekleyenSiparis = JSON.parse(bekleyenSiparisStr);
          console.log('[Sipariş] Bekleyen sipariş bulundu:', bekleyenSiparis);
          
          // Eğer ürün listesi boşsa sipariş oluşturma
          if (!bekleyenSiparis.urunler || bekleyenSiparis.urunler.length === 0) {
            console.log('[Sipariş] Ürün listesi boş, sipariş oluşturulmayacak');
            sessionStorage.removeItem('bekleyen_siparis');
            return;
          }
          
          // Sipariş oluştur - Tüm verileri gönder (user context'e bağımlı değil)
          const siparisData = {
            user_id: bekleyenSiparis.user_id,
            firma: bekleyenSiparis.firma,
            cari_kodu: bekleyenSiparis.cari_kodu,
            cari_unvan: bekleyenSiparis.cari_unvan,
            musteri_adi: bekleyenSiparis.cari_unvan || 'Müşteri',
            panel_kodu: bekleyenSiparis.panel_kodu,
            siparis_kaynagi: 'panel_musteri',
            urunler: bekleyenSiparis.urunler.map(item => ({
              sku: item.sku,
              urun_adi: item.urun_adi,
              miktar: item.miktar,
              birim_fiyat: item.fiyat,
              liste_fiyat: item.liste_fiyat || item.fiyat,
              iskonto_orani: item.iskonto_orani || 0,
              toplam_fiyat: (item.fiyat || 0) * (item.miktar || 1),
              olcu: item.olcu,
              size: item.size
            })),
            teslimat_adresi: bekleyenSiparis.teslimatAdresi,
            fatura_adresi: bekleyenSiparis.faturaAdresi || bekleyenSiparis.teslimatAdresi,
            kargo_tipi: bekleyenSiparis.kargoTipi,
            kargo_firma: bekleyenSiparis.kargoFirma,
            kargo_ucreti: bekleyenSiparis.kargoUcreti || 0,
            odeme_tipi: 'kredi_karti',
            ara_toplam: bekleyenSiparis.araToplam || bekleyenSiparis.genelToplam,
            kdv_tutar: bekleyenSiparis.kdvTutar || 0,
            genel_toplam: parseFloat(total) || bekleyenSiparis.genelToplam,
            siparis_notu: bekleyenSiparis.siparisNotu || '',
            durum: 'beklemede',  // Sipariş beklemede - ödeme bilgisi odeme_bilgileri içinde
            // 🆕 Depo seçimleri (transfer için)
            secilen_depolar: bekleyenSiparis.secilen_depolar || {},
            // Ödeme bilgileri
            odeme_bilgileri: {
              tip: 'kredi_karti',
              invoice_id: invoiceId,
              auth_code: authCode,
              tutar: parseFloat(total) || 0,
              taksit: taksitSayisi,
              banka: banka,
              kart_sahibi: kartSahibi,
              tarih: new Date().toISOString()
            }
          };

          console.log('[Sipariş] Sipariş oluşturuluyor...', siparisData);
          
          const response = await axios.post(`${API}/panel/siparis-olustur`, siparisData);
          console.log('[Sipariş] API Response:', response.data);
          
          if (response.data.success) {
            console.log('[Sipariş] ✅ Sipariş başarıyla oluşturuldu:', response.data.siparis_no);
            // SessionStorage'ı temizle
            sessionStorage.removeItem('bekleyen_siparis');
            
            // Sipariş oluştuktan sonra sepeti temizle
            const userId = bekleyenSiparis.user_id;
            if (userId && !sepetTemizlendiRef.current) {
              sepetTemizlendiRef.current = true;
              try {
                await axios.delete(`${API}/panel/sepet-temizle/${userId}`);
                console.log('[Sepet] Sipariş oluşturuldu, sepet temizlendi');
                // Sepet context'ini güncelle
                window.dispatchEvent(new Event('sepetGuncellendi'));
              } catch (sepetErr) {
                console.error('[Sepet] Temizleme hatası:', sepetErr);
              }
            }
          } else {
            console.error('[Sipariş] ❌ Sipariş oluşturulamadı:', response.data.error);
          }
        } catch (err) {
          console.error('[Sipariş] ❌ Sipariş oluşturma hatası:', err.response?.data || err.message || err);
        }
      }
    };
    
    siparisOlustur();
  }, [isSuccess, isSiparisOdemesi, siparisTipi, total, invoiceId, authCode, taksitSayisi, banka, kartSahibi]);

  // Eğer bekleyen sipariş yoksa ama sepet ödemesi ise yine de sepeti temizle
  useEffect(() => {
    const temizleSepet = async () => {
      // Sadece bekleyen sipariş yoksa ve henüz temizlenmediyse çalışsın
      const bekleyenSiparisStr = sessionStorage.getItem('bekleyen_siparis');
      if (isSuccess && isSiparisOdemesi && user?.id && !sepetTemizlendiRef.current && !bekleyenSiparisStr) {
        sepetTemizlendiRef.current = true;
        try {
          await axios.delete(`${API}/panel/sepet-temizle/${user.id}`);
          console.log('[Sepet] Sipariş tamamlandı, sepet temizlendi (bekleyen sipariş yok)');
          // Sepet context'ini güncelle
          window.dispatchEvent(new Event('sepetGuncellendi'));
        } catch (err) {
          console.error('[Sepet] Temizleme hatası:', err);
        }
      }
    };
    temizleSepet();
  }, [isSuccess, isSiparisOdemesi, user?.id]);

  // MİS aktivite kaydı
  useEffect(() => {
    if (isSuccess && isMusteriHook && !odemeKaydedildiRef.current) {
      odemeKaydedildiRef.current = true;
      siparisKaydet(orderNo, parseFloat(total) || 0, 0, 'kredi_karti');
      donusumAdimiKaydet('siparis', {
        sku: orderNo,
        urun_adi: 'Kredi Kartı Ödemesi',
        fiyat: parseFloat(total) || 0,
        miktar: 1
      }, {
        siparis_no: orderNo,
        odeme_tipi: 'kredi_karti',
        taksit_sayisi: taksitSayisi
      });
    }
  }, [isSuccess, isMusteriHook, orderNo, total, taksitSayisi, siparisKaydet, donusumAdimiKaydet]);

  // ==================== BAŞARILI ÖDEME ====================
  if (isSuccess) {
    
    // ========== MÜŞTERİ DEKONT GÖRÜNÜMÜ ==========
    if (isMusteri) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-zinc-100 py-4 px-3 print:bg-white print:py-0">
          <div className="max-w-md mx-auto">
            
            {/* Yazdır Butonu */}
            <div className="flex justify-between mb-3 print:hidden">
              <button
                onClick={() => navigate('/panel/anasayfa')}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium text-gray-700 shadow-sm"
              >
                <ArrowLeft size={14} />
                Geri
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 text-xs font-bold shadow-md"
              >
                <Printer size={14} />
                Yazdır
              </button>
            </div>

            {/* Dekont Kartı */}
            <div className="bg-white border border-slate-200 shadow-lg rounded-lg overflow-hidden print:shadow-none print:border print:border-black print:rounded-none">
              
              {/* Firma Başlığı - Print'te beyaz arka plan */}
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-4 py-3 print:bg-white print:border-b-2 print:border-black">
                <div className="flex justify-between items-start">
                  <div>
                    <h1 className="text-sm font-bold text-white print:text-black leading-tight">{firmaUnvan}</h1>
                    <p className="text-slate-400 text-[10px] mt-0.5 print:text-gray-600 leading-tight">{firmaAdres}</p>
                    <p className="text-slate-500 text-[9px] print:text-gray-500">V.D: {firmaVergiDairesi} | {firmaVergiNo}</p>
                  </div>
                  <div className="text-right">
                    <h2 className="text-xs font-bold text-white print:text-black">ÖDEME DEKONTU</h2>
                    <p className="text-slate-400 text-[9px] print:text-gray-600">Kredi Kartı İşlemi</p>
                  </div>
                </div>
              </div>

              {/* Sipariş Bildirimi (Sepet ödemesi ise) - Print'te beyaz */}
              {isSiparisOdemesi && (
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 print:bg-white print:border-b print:border-green-600">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center print:bg-green-100">
                      <BadgeCheck size={18} className="text-white print:text-green-600" />
                    </div>
                    <div>
                      <p className="text-white print:text-green-700 font-bold text-sm">Siparişiniz Alındı!</p>
                      <p className="text-green-100 print:text-green-600 text-[10px]">Sipariş No: <span className="font-bold">{orderNo}</span></p>
                    </div>
                  </div>
                </div>
              )}

              {/* Başarı Durumu (Cari ödeme ise) */}
              {!isSiparisOdemesi && (
                <div className="bg-green-50 border-b border-green-200 px-4 py-2 flex items-center gap-2">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="font-semibold text-green-800 text-xs">İşlem Başarılı</span>
                </div>
              )}

              {/* Dekont İçeriği */}
              <div className="p-4">
                
                {/* Müşteri Bilgileri */}
                {(cariUnvani || musteriNo) && (
                  <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-2.5 mb-3 print:bg-white print:border-black">
                    <div className="flex items-center gap-2">
                      <Building size={14} className="text-indigo-600 print:text-black" />
                      <div className="flex-1 min-w-0">
                        <p className="text-[9px] text-indigo-600 print:text-gray-600 font-semibold uppercase">MÜŞTERİ</p>
                        <p className="font-bold text-slate-800 text-xs truncate">{cariUnvani || user?.cari_unvan || musteriAdi}</p>
                        {musteriNo && <p className="text-[10px] text-slate-500">Müşteri No: <span className="font-semibold">{musteriNo}</span></p>}
                      </div>
                    </div>
                  </div>
                )}

                {/* İşlem Detayları */}
                <table className="w-full text-[11px] mb-3">
                  <tbody>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500 flex items-center gap-1"><Calendar size={10} /> Tarih</td>
                      <td className="py-1.5 font-medium text-gray-800 text-right">{tarih}</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500 flex items-center gap-1"><Clock size={10} /> Saat</td>
                      <td className="py-1.5 font-medium text-gray-800 text-right">{saat}</td>
                    </tr>
                    <tr className="border-b border-gray-100 bg-blue-50 print:bg-gray-100">
                      <td className="py-1.5 text-gray-600 font-medium flex items-center gap-1"><CreditCard size={10} /> Kart Sahibi</td>
                      <td className="py-1.5 font-bold text-gray-900 text-right">{kartSahibi || '-'}</td>
                    </tr>
                    {banka && (
                      <tr className="border-b border-gray-100 bg-purple-50 print:bg-gray-50">
                        <td className="py-1.5 text-purple-600 font-medium">Banka</td>
                        <td className="py-1.5 font-semibold text-purple-700 text-right flex items-center justify-end gap-2">
                          {getBankLogo(banka) && (
                            <img 
                              src={getBankLogo(banka)} 
                              alt={banka} 
                              className="h-5 w-auto object-contain"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{banka}</span>
                          {kartTipi && <span className="text-[9px] text-gray-500">({kartTipi})</span>}
                        </td>
                      </tr>
                    )}
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500 flex items-center gap-1"><Hash size={10} /> Referans</td>
                      <td className="py-1.5 font-mono text-gray-800 text-right text-[10px]">{invoiceId}</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500 flex items-center gap-1"><Shield size={10} /> Onay Kodu</td>
                      <td className="py-1.5 font-mono font-bold text-gray-900 text-right">{authCode}</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500">Ödeme</td>
                      <td className="py-1.5 font-medium text-gray-800 text-right">Kredi Kartı - 3D</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-1.5 text-gray-500">Taksit</td>
                      <td className="py-1.5 font-medium text-right">
                        {taksitSayisi <= 1 ? (
                          <span className="text-green-700 font-bold">Tek Çekim</span>
                        ) : (
                          <span className="text-blue-700 font-bold">{taksitSayisi} Taksit</span>
                        )}
                      </td>
                    </tr>
                    {islemYapan && (
                      <tr className="border-b border-gray-100">
                        <td className="py-1.5 text-gray-500 flex items-center gap-1"><User size={10} /> İşlemi Yapan</td>
                        <td className="py-1.5 font-medium text-gray-800 text-right">{islemYapan}</td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Tutar Kutusu - Print'te border ile */}
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white rounded-lg p-3 mb-3 print:bg-white print:text-black print:border-2 print:border-black print:rounded-none">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-300 text-[10px] font-medium print:text-gray-600">ÖDENEN TUTAR</span>
                    <span className="text-xl font-black">{formatTutar(total)} ₺</span>
                  </div>
                  {taksitSayisi > 1 && (
                    <p className="text-slate-400 text-[9px] mt-1 text-right print:text-gray-500">
                      {taksitSayisi} x {formatTutar(parseFloat(total) / taksitSayisi)} ₺
                    </p>
                  )}
                </div>

                {/* Sipariş Bilgilendirmesi */}
                {isSiparisOdemesi && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mb-3 print:bg-white print:border-amber-400">
                    <div className="flex gap-2">
                      <Package size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-[10px] text-amber-800">
                        Siparişiniz hazırlanacak ve kargoya verilecektir. 
                        <strong> Siparişlerim</strong> sayfasından takip edebilirsiniz.
                      </p>
                    </div>
                  </div>
                )}

                {/* QNB Pay */}
                <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3 print:bg-white">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center text-[8px] font-black text-white print:bg-white print:text-red-600 print:border print:border-red-600">QNB</div>
                    <p className="text-[9px] text-gray-600">QNB Finansbank güvenli ödeme | 3D Secure</p>
                  </div>
                </div>

                {/* Alt Bilgi */}
                <div className="text-[8px] text-gray-400 space-y-0.5">
                  <p>• Kart ekstrenizde &quot;{firmaMeta.ekstre}&quot; olarak görünecektir.</p>
                  <p>• Bu dekont yasal belge niteliğindedir.</p>
                </div>
              </div>

              {/* Alt Bilgi */}
              <div className="bg-slate-50 px-4 py-2 print:bg-white print:border-t print:border-black">
                <div className="flex justify-between text-[9px] text-slate-500 print:text-black">
                  <span>{firmaMeta.email}</span>
                  <span>{tarih} {saat}</span>
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div className="mt-3 grid grid-cols-2 gap-2 print:hidden">
              <button
                onClick={() => navigate('/panel/anasayfa')}
                className="py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-lg font-semibold text-xs transition shadow-md flex items-center justify-center gap-1.5"
              >
                <Home size={14} />
                Anasayfa
              </button>
              {isSiparisOdemesi ? (
                <button
                  onClick={() => navigate('/panel/siparislerim')}
                  className="py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold text-xs transition shadow-md flex items-center justify-center gap-1.5"
                >
                  <ShoppingBag size={14} />
                  Siparişlerim
                </button>
              ) : (
                <button
                  onClick={() => navigate('/panel/musteri-cari')}
                  className="py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold text-xs transition shadow-md flex items-center justify-center gap-1.5"
                >
                  <FileText size={14} />
                  Hesap Hareketleri
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    // ========== FİRMA/ADMİN DEKONT (A5 Boyutu) ==========
    return (
      <div className="min-h-screen bg-gray-100 py-4 px-3 print:bg-white print:py-0">
        <div className="max-w-[148mm] mx-auto">
          
          {/* Butonlar */}
          <div className="flex justify-between mb-3 print:hidden">
            <button
              onClick={() => navigate('/panel/kredi-karti-provizyon')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium text-gray-700"
            >
              <ArrowLeft size={14} />
              Provizyonlar
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
            >
              <Printer size={14} />
              Yazdır / PDF
            </button>
          </div>

          {/* Dekont */}
          <div className="bg-white border border-gray-300 shadow-sm print:shadow-none print:border print:border-black text-[11px]">
            
            {/* Üst Başlık - Print'te beyaz */}
            <div className="border-b-2 border-black p-4 bg-white">
              <div className="flex justify-between items-start">
                <div>
                  <h1 className="text-sm font-bold text-gray-900">{firmaUnvan}</h1>
                  <p className="text-[10px] text-gray-600 mt-0.5">{firmaAdres}</p>
                  <p className="text-[9px] text-gray-500">V.D: {firmaVergiDairesi} | V.No: {firmaVergiNo}</p>
                </div>
                <div className="text-right">
                  <h2 className="text-sm font-bold text-gray-900">ÖDEME DEKONTU</h2>
                  <p className="text-[9px] text-gray-500 mt-0.5">Kredi Kartı İşlem Belgesi</p>
                  <p className="text-[9px] text-gray-400">Firma: {firma}</p>
                </div>
              </div>
            </div>

            {/* İşlem Durumu */}
            <div className="bg-green-50 border-b border-green-200 px-4 py-2 print:bg-white">
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full print:border print:border-green-500"></div>
                <span className="font-semibold text-green-800 text-xs">İşlem Başarılı</span>
              </div>
            </div>

            {/* Ana İçerik */}
            <div className="p-4">
              
              <table className="w-full mb-4">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600 w-1/3">İşlem Tarihi</td>
                    <td className="py-1.5 font-medium text-gray-900">{tarih}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">İşlem Saati</td>
                    <td className="py-1.5 font-medium text-gray-900">{saat}</td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-blue-50 print:bg-gray-100">
                    <td className="py-1.5 text-gray-600 font-semibold">Kart Sahibi</td>
                    <td className="py-1.5 font-bold text-gray-900">{kartSahibi || '-'}</td>
                  </tr>
                  {banka && (
                    <tr className="border-b border-gray-200 bg-purple-50 print:bg-gray-50">
                      <td className="py-1.5 text-purple-700 font-semibold">Banka</td>
                      <td className="py-1.5 font-bold text-purple-800 flex items-center gap-2">
                        {getBankLogo(banka) && (
                          <img 
                            src={getBankLogo(banka)} 
                            alt={banka} 
                            className="h-6 w-auto object-contain"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span>{banka}</span>
                        {kartTipi && <span className="font-normal text-gray-500">({kartTipi})</span>}
                      </td>
                    </tr>
                  )}
                  {(cariKodu || cariUnvani) && (
                    <>
                      <tr className="border-b border-gray-200 bg-slate-100 print:bg-gray-50">
                        <td className="py-1.5 text-gray-600 font-semibold">Müşteri No</td>
                        <td className="py-1.5 font-bold text-slate-800">{musteriNo || '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-slate-100 print:bg-gray-50">
                        <td className="py-1.5 text-gray-600 font-semibold">Cari Ünvanı</td>
                        <td className="py-1.5 font-bold text-slate-800">{cariUnvani || musteriAdi || '-'}</td>
                      </tr>
                    </>
                  )}
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">Sipariş No</td>
                    <td className="py-1.5 font-mono font-medium text-gray-900 text-[10px]">{orderNo}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">Referans No</td>
                    <td className="py-1.5 font-mono font-medium text-gray-900">{invoiceId}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">Banka Onay Kodu</td>
                    <td className="py-1.5 font-mono font-bold text-gray-900">{authCode}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">Ödeme Yöntemi</td>
                    <td className="py-1.5 font-medium text-gray-900">Kredi Kartı - 3D Secure</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-1.5 text-gray-600">Taksit</td>
                    <td className="py-1.5 font-medium text-gray-900">
                      {taksitSayisi <= 1 ? (
                        <span className="text-green-700 font-bold">Tek Çekim</span>
                      ) : (
                        <span>
                          <span className="font-bold text-blue-700">{taksitSayisi} Taksit</span>
                          <span className="text-gray-500 ml-1 text-[10px]">
                            ({formatTutar(parseFloat(total) / taksitSayisi)} x {taksitSayisi})
                          </span>
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              {/* Tutar - Print'te border */}
              <div className="border-2 border-gray-900 p-3 mb-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium text-xs">ÖDENEN TUTAR</span>
                  <span className="text-xl font-bold text-gray-900">{formatTutar(total)} TRY</span>
                </div>
              </div>
              
              {islemYapan && (
                <div className="text-[10px] text-gray-500 text-right mb-3">
                  İşlemi yapan: <span className="font-medium text-gray-700">{islemYapan}</span>
                </div>
              )}

              {/* QNB Pay */}
              <div className="bg-gray-50 border border-gray-200 p-3 mb-3 print:bg-white">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-red-600 rounded flex items-center justify-center text-[8px] font-bold text-white print:bg-white print:text-red-600 print:border print:border-red-600">QNB</div>
                  <div className="text-[9px] text-gray-600">
                    <p className="font-medium text-gray-700">QNB Pay - Güvenli Ödeme</p>
                    <p>3D Secure protokolü ile korunmaktadır.</p>
                  </div>
                </div>
              </div>

              {/* Bilgi */}
              <div className="text-[9px] text-gray-500 mb-3">
                <p>• Bu belge yasal belge niteliği taşımaktadır.</p>
              </div>

              {/* Alt */}
              <div className="border-t-2 border-gray-300 pt-3">
                <div className="flex justify-between text-[9px] text-gray-500">
                  <div>
                    <p className="font-medium text-gray-700">{firmaUnvan}</p>
                    <p>{firmaMeta.email}</p>
                  </div>
                  <div className="text-right">
                    <p>Belge: {invoiceId}</p>
                    <p>{tarih} {saat}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* İmza */}
            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 print:bg-white">
              <div className="flex justify-between">
                <div className="text-center">
                  <div className="w-24 border-b border-gray-400 mb-1"></div>
                  <p className="text-[9px] text-gray-500">Müşteri İmzası</p>
                </div>
                <div className="text-center">
                  <div className="w-24 border-b border-gray-400 mb-1"></div>
                  <p className="text-[9px] text-gray-500">Yetkili İmzası</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== BAŞARISIZ ÖDEME ====================
  // Müşteri için kompakt başarısız ekranı (eski tasarım)
  if (isMusteri) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full">
          <div className="bg-white border border-red-100 rounded-2xl shadow-xl overflow-hidden">
            
            <div className="bg-gradient-to-br from-red-500 to-rose-600 px-6 py-8 text-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center mx-auto mb-3">
                <XCircle size={36} className="text-white" />
              </div>
              <h1 className="text-xl font-bold text-white mb-1">Ödeme Başarısız</h1>
              <p className="text-red-100 text-sm">İşlem tamamlanamadı</p>
            </div>

            <div className="p-5">
              {errorMsg && (
                <div className={`rounded-lg p-3 mb-4 border ${
                  hataKategori === 'kart' ? 'bg-rose-50 border-rose-200'
                  : hataKategori === 'sistem' ? 'bg-amber-50 border-amber-200'
                  : 'bg-red-50 border-red-200'
                }`}>
                  {/* Kategori rozeti: kart mı sistem mi */}
                  {hataKategoriEtiket && (
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                        hataKategori === 'kart' ? 'bg-rose-600 text-white'
                        : hataKategori === 'sistem' ? 'bg-amber-600 text-white'
                        : 'bg-slate-500 text-white'
                      }`}>
                        {hataKategori === 'kart' ? '💳 ' : hataKategori === 'sistem' ? '⚙️ ' : '❓ '}{hataKategoriEtiket}
                      </span>
                      {qnbKod && qnbKod !== '0' && <span className="text-[10px] text-gray-400">QNBPay kodu: {qnbKod}</span>}
                    </div>
                  )}
                  {/* Bankanın verdiği gerçek red sebebi */}
                  <p className="text-sm text-red-700 font-semibold">{decodeURIComponent(errorMsg)}</p>
                  {/* Ne yapmalı önerisi */}
                  {hataOneri && (
                    <p className="text-[11px] text-gray-600 mt-1.5 leading-snug">👉 {hataOneri}</p>
                  )}
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <table className="w-full text-xs">
                  <tbody>
                    <tr><td className="py-1.5 text-gray-600">Tarih</td><td className="py-1.5 text-right font-medium">{tarih} {saat}</td></tr>
                    {kartSahibi && <tr><td className="py-1.5 text-gray-600">Kart Sahibi</td><td className="py-1.5 text-right font-medium">{kartSahibi}</td></tr>}
                    {(cariUnvani || cariKodu) && (
                      <tr><td className="py-1.5 text-gray-600">Cari</td><td className="py-1.5 text-right font-medium">{cariUnvani || musteriAdi}</td></tr>
                    )}
                    {total && parseFloat(total) > 0 && (
                      <tr><td className="py-1.5 text-gray-600">Tutar</td><td className="py-1.5 text-right font-bold text-red-600">{formatTutar(total)} TRY</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="space-y-2">
                <button
                  data-testid="odeme-fail-tekrar-dene-btn"
                  onClick={() => navigate('/panel/sepetim')}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-lg font-semibold text-sm transition shadow-md flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={16} />
                  Tekrar Dene
                </button>
                <button
                  data-testid="odeme-fail-anasayfa-btn"
                  onClick={() => navigate('/panel/anasayfa')}
                  className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium text-sm transition flex items-center justify-center gap-2"
                >
                  <Home size={16} />
                  Anasayfa
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FİRMA/ADMİN BAŞARISIZ DEKONT — Success A5 tasarımı ile birebir aynı, sadece kırmızı status banner + hata kutusu
  return (
    <div className="min-h-screen bg-gray-100 py-4 px-3 print:bg-white print:py-0">
      <div className="max-w-[148mm] mx-auto">
        
        {/* Butonlar */}
        <div className="flex justify-between mb-3 print:hidden">
          <button
            data-testid="odeme-fail-provizyon-don-btn"
            onClick={() => navigate('/panel/kredi-karti-provizyon')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-xs font-medium text-gray-700"
          >
            <ArrowLeft size={14} />
            Provizyonlar
          </button>
          <button
            data-testid="odeme-fail-yazdir-btn"
            onClick={handlePrint}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium"
          >
            <Printer size={14} />
            Yazdır / PDF
          </button>
        </div>

        {/* Dekont — başarısız varyantı */}
        <div className="bg-white border border-gray-300 shadow-sm print:shadow-none print:border print:border-black text-[11px]" data-testid="odeme-fail-dekont">
          
          {/* Üst Başlık - Firma bilgileri */}
          <div className="border-b-2 border-black p-4 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-sm font-bold text-gray-900">{firmaUnvan}</h1>
                <p className="text-[10px] text-gray-600 mt-0.5">{firmaAdres}</p>
                <p className="text-[9px] text-gray-500">V.D: {firmaVergiDairesi} | V.No: {firmaVergiNo}</p>
              </div>
              <div className="text-right">
                <h2 className="text-sm font-bold text-gray-900">ÖDEME DEKONTU</h2>
                <p className="text-[9px] text-gray-500 mt-0.5">Kredi Kartı İşlem Belgesi</p>
                <p className="text-[9px] text-gray-400">Firma: {firma}</p>
              </div>
            </div>
          </div>

          {/* İşlem Durumu — KIRMIZI */}
          <div className="bg-red-50 border-b border-red-200 px-4 py-2 print:bg-white">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 bg-red-500 rounded-full print:border print:border-red-500"></div>
              <span className="font-semibold text-red-800 text-xs">İşlem Başarısız</span>
            </div>
          </div>

          {/* Ana İçerik */}
          <div className="p-4">
            
            {/* Hata Mesajı kutusu */}
            {errorMsg && (
              <div className="bg-red-50 border border-red-300 rounded p-3 mb-3" data-testid="odeme-fail-hata-kutusu">
                <p className="text-[10px] text-red-700 font-bold uppercase mb-1">Hata Açıklaması</p>
                <p className="text-xs text-red-800 leading-relaxed">{decodeURIComponent(errorMsg)}</p>
              </div>
            )}
            
            <table className="w-full mb-4">
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600 w-1/3">İşlem Tarihi</td>
                  <td className="py-1.5 font-medium text-gray-900">{tarih}</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600">İşlem Saati</td>
                  <td className="py-1.5 font-medium text-gray-900">{saat}</td>
                </tr>
                {kartSahibi && (
                  <tr className="border-b border-gray-200 bg-blue-50 print:bg-gray-100">
                    <td className="py-1.5 text-gray-600 font-semibold">Kart Sahibi</td>
                    <td className="py-1.5 font-bold text-gray-900">{kartSahibi}</td>
                  </tr>
                )}
                {banka && (
                  <tr className="border-b border-gray-200 bg-purple-50 print:bg-gray-50">
                    <td className="py-1.5 text-purple-700 font-semibold">Banka</td>
                    <td className="py-1.5 font-bold text-purple-800 flex items-center gap-2">
                      {getBankLogo(banka) && (
                        <img src={getBankLogo(banka)} alt={banka} className="h-6 w-auto object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <span>{banka}</span>
                      {kartTipi && <span className="font-normal text-gray-500">({kartTipi})</span>}
                    </td>
                  </tr>
                )}
                {(cariKodu || cariUnvani) && (
                  <>
                    <tr className="border-b border-gray-200 bg-slate-100 print:bg-gray-50">
                      <td className="py-1.5 text-gray-600 font-semibold">Müşteri No</td>
                      <td className="py-1.5 font-bold text-slate-800">{musteriNo || '-'}</td>
                    </tr>
                    <tr className="border-b border-gray-200 bg-slate-100 print:bg-gray-50">
                      <td className="py-1.5 text-gray-600 font-semibold">Cari Ünvanı</td>
                      <td className="py-1.5 font-bold text-slate-800">{cariUnvani || musteriAdi || '-'}</td>
                    </tr>
                  </>
                )}
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600">Sipariş No</td>
                  <td className="py-1.5 font-mono font-medium text-gray-900 text-[10px]">{orderNo}</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600">Referans No</td>
                  <td className="py-1.5 font-mono font-medium text-gray-900">{invoiceId}</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600">Ödeme Yöntemi</td>
                  <td className="py-1.5 font-medium text-gray-900">Kredi Kartı - 3D Secure</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="py-1.5 text-gray-600">Taksit</td>
                  <td className="py-1.5 font-medium text-gray-900">
                    {taksitSayisi <= 1 ? 'Tek Çekim' : `${taksitSayisi} Taksit`}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Tutar — Kırmızı border ile */}
            {total && parseFloat(total) > 0 && (
              <div className="border-2 border-red-700 p-3 mb-3 bg-red-50 print:bg-white">
                <div className="flex justify-between items-center">
                  <span className="text-red-700 font-medium text-xs">DENENEN TUTAR (Tahsil Edilemedi)</span>
                  <span className="text-xl font-bold text-red-700">{formatTutar(total)} TRY</span>
                </div>
              </div>
            )}

            {islemYapan && (
              <div className="text-[10px] text-gray-500 text-right mb-3">
                İşlemi yapan: <span className="font-medium text-gray-700">{islemYapan}</span>
              </div>
            )}

            {/* QNB Pay */}
            <div className="bg-gray-50 border border-gray-200 p-3 mb-3 print:bg-white">
              <div className="flex items-start gap-2">
                <div className="w-7 h-7 bg-red-600 rounded flex items-center justify-center text-[8px] font-bold text-white print:bg-white print:text-red-600 print:border print:border-red-600">QNB</div>
                <div className="text-[9px] text-gray-600">
                  <p className="font-medium text-gray-700">QNB Pay - Güvenli Ödeme</p>
                  <p>3D Secure protokolü ile korunmaktadır.</p>
                </div>
              </div>
            </div>

            {/* Bilgi */}
            <div className="text-[9px] text-gray-500 mb-3">
              <p>• Bu belge başarısız işlem kaydı olarak yasal saklama kapsamındadır.</p>
            </div>

            {/* Alt */}
            <div className="border-t-2 border-gray-300 pt-3">
              <div className="flex justify-between text-[9px] text-gray-500">
                <div>
                  <p className="font-medium text-gray-700">{firmaUnvan}</p>
                  <p>{firmaMeta.email}</p>
                </div>
                <div className="text-right">
                  <p>Belge: {invoiceId}</p>
                  <p>{tarih} {saat}</p>
                </div>
              </div>
            </div>
          </div>

          {/* İmza */}
          <div className="border-t border-gray-200 px-4 py-3 bg-gray-50 print:bg-white">
            <div className="flex justify-between">
              <div className="text-center">
                <div className="w-24 border-b border-gray-400 mb-1"></div>
                <p className="text-[9px] text-gray-500">Müşteri İmzası</p>
              </div>
              <div className="text-center">
                <div className="w-24 border-b border-gray-400 mb-1"></div>
                <p className="text-[9px] text-gray-500">Yetkili İmzası</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdemeSonuc;
